import React, { useState, useEffect } from "react";

import { Checkbox, CheckboxField, CheckboxGroup } from "V2.0/common/checkbox";
import { Description, Field, Label } from "V2.0/common/fieldset";
import { Input } from "V2.0/common/input";
import { Button } from "V2.0/common/button";
import InputComponent from "V2.0/components/InputComponent";
import { message } from "antd";
import { useOutletContext } from "react-router-dom";
import { GHLIntegration } from "./GHLIntegration";
import { IntegrationShell } from "../common/Shell";
import * as thirdpartyService from "V2.0/services/thirdpartyService";
import { GHLConnect } from "V2.0/HomePages/HomeIntegrations/GHL";
import { showErrorToast } from "V2.0/utils";

function GhlCalendarSettings(props) {
  const { thirdPartySettings, updateThirdPartySettings, loading, isViewOnly } =
    props;

  const [calendarSettings, setCalendarSettings] = useState({
    calendarId: "",
    appointmentName: "",
  });

  useEffect(() => {
    if (thirdPartySettings) {
      setCalendarSettings(
        thirdPartySettings.calendarSettings.ghlCalendarSettings,
      );
    }
  }, [thirdPartySettings]);

  const onSave = () => {
    updateThirdPartySettings({
      calendarSettings: {
        ghlCalendarSettings: calendarSettings,
      },
    });
  };

  return (
    <div className="border rounded-lg mt-4 flex flex-col gap-y-8 p-8">
      <div className="flex flex-col gap-y-8">
        <InputComponent
          type="text"
          label="Appointment Name"
          description="Specify a unique name for meetings booked by the Assistant"
          value={calendarSettings?.appointmentName}
          onChange={(e) => {
            setCalendarSettings((prevState) => ({
              ...prevState,
              appointmentName: e.target.value,
            }));
          }}
          disabled={isViewOnly}
        />
        <InputComponent
          type="text"
          label="Calendar Id"
          description='Paste your "Personal" or "event" calendar Id from GHL'
          value={calendarSettings?.calendarId}
          onChange={(e) => {
            setCalendarSettings((prevState) => ({
              ...prevState,
              calendarId: e.target.value,
            }));
          }}
          disabled={isViewOnly}
        />
      </div>
      {!isViewOnly && (
        <div className="self-end pb-4">
          <Button
            onClick={onSave}
            disabled={loading}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

function GhlCrmSettings(props) {
  const { thirdPartySettings, fetchThirdPartySettings, projectId, isViewOnly } =
    props;
  const [loading, setLoading] = useState(false);

  async function updateThirdPartySettings(payload) {
    setLoading(true);
    try {
      const response = await thirdpartyService.thirdpartyUpdate(
        payload,
        projectId,
      );
      if (response.status === 200) {
        fetchThirdPartySettings();
        setLoading(false);
        message.success("Settings updated");
      }
    } catch (e) {
      setLoading(false);
      showErrorToast(e, "Failed to update settings");
    }
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center">
        <p className="font-semibold text-md">GHL Calendar</p>
        <div>
          {!isViewOnly && (
            <GHLConnect
              projectId={projectId}
              label="Reconnect"
            />
          )}
        </div>
      </div>
      <div className="">
        <GhlCalendarSettings
          thirdPartySettings={thirdPartySettings}
          loading={loading}
          updateThirdPartySettings={updateThirdPartySettings}
          isViewOnly={isViewOnly}
        />
      </div>
    </div>
  );
}

interface ThirdPartySettings {
  crmSettings: {
    crmProvider: "ghlCRM" | "zohoCRM" | "unassigned";
  };
}

function GHLCRM() {
  const { projectId, isViewOnly } = useOutletContext<{
    projectId: string;
    isViewOnly: boolean;
  }>();
  const [loading, setLoading] = useState(true);
  const [thirdPartySettings, setThirdPartySettings] = useState(null);

  async function fetchThirdPartySettings() {
    try {
      const response = await thirdpartyService.getthirdpartyData(projectId);
      if (response.status === 200) {
        setThirdPartySettings(response.data);
        setLoading(false);
      }
    } catch (error) {
      showErrorToast(error, "Failed to get settings");
      setLoading(false);
    }
  }

  useEffect(() => {
    if (projectId) {
      fetchThirdPartySettings();
    }
  }, [projectId]);

  if (loading) {
    return;
  }

  if (thirdPartySettings?.crmSettings?.crmProvider === "ghlCRM") {
    return (
      <IntegrationShell>
        <GhlCrmSettings
          fetchThirdPartySettings={fetchThirdPartySettings}
          projectId={projectId}
          thirdPartySettings={thirdPartySettings}
          isViewOnly={isViewOnly}
        />
      </IntegrationShell>
    );
  }

  return (
    <GHLIntegration
      projectId={projectId}
      isViewOnly={isViewOnly}
    />
  );
}

export default GHLCRM;
